import React, { useState } from "react";

// ─── Helpers ────────────────────────────────────────────────────
function parseJson(raw) {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try { return JSON.parse(raw); } catch { return null; }
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

const LABELS = {
  FIRST_NAME: "First Name", LAST_NAME: "Last Name", MOBILE_NO: "Mobile No",
  CONTACT_NO: "Contact No", ADDRESS: "Address", CITY_AREA_VILLAGE: "City",
  PIN_CODE: "PIN Code", TALUKA: "Taluka", DISTRICT: "District",
  STATE: "State", DOB: "DOB", Sevakcode: "Sevak Code", image_url: "Image",
  tag: "Tag", medical_insurance: "Medical Insurance",
  medical_insurance_company: "Medical Company",
  accident_insurance: "Accident Insurance",
  accident_insurance_company: "Accident Company",
  occupation_type: "Occupation", business_name: "Business Name",
  business_type: "Business Type", business_field: "Business Field",
  job_company_name: "Company", job_role: "Role", job_field: "Job Field", has_children: "Has Children",
};

function fieldLabel(key) {
  return LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Shared: Summary Card ───────────────────────────────────────
function SummaryCard({ label, value, bg, border, textColor = "" }) {
  return (
    <div className={`p-4 rounded-lg border ${bg} ${border}`}>
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`text-2xl font-bold ${textColor}`}>{value}</p>
    </div>
  );
}

// =================================================================
// TAB 1: Recent Edits
// =================================================================
export function RecentEditsTab({ data }) {
  const [expandedId, setExpandedId] = useState(null);

  if (!data || data.length === 0) {
    return <p className="text-gray-500 text-center py-8">No recent edits found.</p>;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Recent User Edits</h2>
      <div className="bg-white shadow rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">When</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Edited By</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sevak</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Changes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.map((log) => {
              const oldVal = parseJson(log.old_value);
              const newVal = parseJson(log.new_value);
              const isExpanded = expandedId === log.id;
              return (
                <React.Fragment key={log.id}>
                  <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : log.id)}>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{fmtDate(log.created_at)}</td>
                    <td className="px-4 py-3 text-sm"><span className="font-medium">{log.user_email || log.user_type}</span></td>
                    <td className="px-4 py-3 text-sm">
                      <span className="font-mono text-purple-700">{log.target_sevak_code || "—"}</span>
                      {log.FIRST_NAME && <span className="ml-2 text-gray-600">{log.FIRST_NAME} {log.LAST_NAME}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded font-semibold ${log.is_self_edit ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}`}>
                        {log.is_self_edit ? "Self Edit" : "Admin Edit"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-blue-600">
                      {oldVal ? `${Object.keys(oldVal).length} field(s) changed` : "View"}
                      <span className="ml-1">{isExpanded ? "▲" : "▼"}</span>
                    </td>
                  </tr>
                  {isExpanded && oldVal && newVal && (
                    <tr>
                      <td colSpan="5" className="px-4 py-3 bg-gray-50">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {Object.keys(oldVal).map((key) => (
                            <div key={key} className="border rounded p-2 bg-white">
                              <p className="text-xs font-semibold text-gray-500 mb-1">{fieldLabel(key)}</p>
                              <p className="text-sm">
                                <span className="line-through text-red-500">{String(oldVal[key] ?? "(empty)")}</span>
                                <span className="mx-2">→</span>
                                <span className="text-green-700 font-medium">{String(newVal[key] ?? "(empty)")}</span>
                              </p>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =================================================================
// TAB 2: Insurance with sub-tabs
// =================================================================
function getCoverage(u) {
  const med = u.medical_insurance === "yes";
  const acc = u.accident_insurance === "yes";
  if (med && acc) return "full";
  if (med || acc) return "partial";
  return "none";
}

function getCoverageBadge(type) {
  if (type === "full") return { text: "Fully Covered", cls: "bg-green-100 text-green-800" };
  if (type === "partial") return { text: "Partially Covered", cls: "bg-yellow-100 text-yellow-800" };
  return { text: "Not Covered", cls: "bg-red-100 text-red-800" };
}

function InsuranceTable({ list, search }) {
  const filtered = list.filter((u) => {
    const q = search.toLowerCase();
    return !q ||
      (u.FIRST_NAME || "").toLowerCase().includes(q) ||
      (u.Sevakcode || "").toLowerCase().includes(q) ||
      (u.DISTRICT || "").toLowerCase().includes(q);
  });

  if (!filtered.length) {
    return <p className="text-gray-500 text-center py-6">No users in this category.</p>;
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-x-auto">
      <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sevak</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">District</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Medical</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Accident</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filtered.map((u) => {
              const badge = getCoverageBadge(getCoverage(u));
              return (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm font-mono text-purple-700">{u.Sevakcode || "—"}</td>
                  <td className="px-4 py-2 text-sm font-medium">{u.FIRST_NAME} {u.LAST_NAME}</td>
                  <td className="px-4 py-2 text-sm text-gray-500">{u.DISTRICT || "N/A"}</td>
                  <td className="px-4 py-2 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${badge.cls}`}>{badge.text}</span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    {u.medical_insurance === "yes"
                      ? <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800">Yes</span>
                      : <span className="px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-500">N/A</span>}
                  </td>
                  <td className="px-4 py-2 text-sm">{u.medical_insurance === "yes" ? (u.medical_insurance_company || "N/A") : "N/A"}</td>
                  <td className="px-4 py-2 text-center">
                    {u.accident_insurance === "yes"
                      ? <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800">Yes</span>
                      : <span className="px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-500">N/A</span>}
                  </td>
                  <td className="px-4 py-2 text-sm">{u.accident_insurance === "yes" ? (u.accident_insurance_company || "N/A") : "N/A"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function InsuranceTab({ data }) {
  const [subTab, setSubTab] = useState("today");
  const [search, setSearch] = useState("");

  const users = data?.users || [];
  const todayEdits = data?.todayEdits || [];

  if (!users.length && !todayEdits.length) {
    return <p className="text-gray-500 text-center py-8">No data available.</p>;
  }

  const fullyCovered = users.filter((u) => getCoverage(u) === "full");
  const partiallyCovered = users.filter((u) => getCoverage(u) === "partial");
  const notCovered = users.filter((u) => getCoverage(u) === "none");

  const SUB_TABS = [
    { key: "today", label: "Today's Edit", count: todayEdits.length },
    { key: "full", label: "Fully Covered", count: fullyCovered.length },
    { key: "partial", label: "Partially Covered", count: partiallyCovered.length },
    { key: "none", label: "Not Covered", count: notCovered.length },
  ];

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Insurance Details</h2>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard label="Total Sevaks" value={users.length} bg="bg-blue-50" border="border-blue-200" />
        <SummaryCard label="Fully Covered" value={fullyCovered.length} bg="bg-green-50" border="border-green-200" textColor="text-green-700" />
        <SummaryCard label="Partially Covered" value={partiallyCovered.length} bg="bg-yellow-50" border="border-yellow-200" textColor="text-yellow-700" />
        <SummaryCard label="Not Covered" value={notCovered.length} bg="bg-red-50" border="border-red-200" textColor="text-red-700" />
      </div>

      {/* Sub-tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-6 overflow-x-auto">
          {SUB_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setSubTab(tab.key); setSearch(""); }}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                subTab === tab.key
                  ? "border-red-500 text-red-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.label}
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${
                subTab === tab.key ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Search for table tabs */}
      {subTab !== "today" && (
        <input
          type="text"
          placeholder="Search by name, sevakcode, or district..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full md:w-80 px-3 py-2 border rounded-md text-sm"
        />
      )}

      {/* Today's Edit sub-tab - users whose insurance was updated today */}
      {subTab === "today" && (
        todayEdits.length > 0 ? (
          <div className="space-y-6">
            {/* Fully Covered today */}
            {(() => {
              const todayFull = todayEdits.filter((u) => getCoverage(u) === "full");
              return todayFull.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded bg-green-100 text-green-800">Fully Covered</span>
                    <span className="text-gray-500">({todayFull.length})</span>
                  </h3>
                  <InsuranceTable list={todayFull} search="" />
                </div>
              );
            })()}

            {/* Not Covered + Partially Covered today */}
            {(() => {
              const todayNotFull = todayEdits.filter((u) => getCoverage(u) !== "full");
              return todayNotFull.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-orange-800 mb-2 flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded bg-orange-100 text-orange-800">Not Covered & Partially Covered</span>
                    <span className="text-gray-500">({todayNotFull.length})</span>
                  </h3>
                  <InsuranceTable list={todayNotFull} search="" />
                </div>
              );
            })()}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No insurance edits today.</p>
        )
      )}

      {subTab === "full" && <InsuranceTable list={fullyCovered} search={search} />}
      {subTab === "partial" && <InsuranceTable list={partiallyCovered} search={search} />}
      {subTab === "none" && <InsuranceTable list={notCovered} search={search} />}
    </div>
  );
}

// =================================================================
// TAB 3: Occupation Summary (card layout like mudat)
// =================================================================
export function OccupationTab({ data }) {
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  if (!data || data.length === 0) {
    return <p className="text-gray-500 text-center py-8">No occupation data found. Users need to fill their occupation details first.</p>;
  }

  const filtered = data.filter((u) => {
    const q = search.toLowerCase();
    return (
      !q ||
      (u.FIRST_NAME || "").toLowerCase().includes(q) ||
      (u.Sevakcode || "").toLowerCase().includes(q) ||
      (u.business_field || "").toLowerCase().includes(q) ||
      (u.job_role || "").toLowerCase().includes(q) ||
      (u.job_field || "").toLowerCase().includes(q)
    );
  });

  const jobCount = data.filter((u) => u.occupation_type === "job").length;
  const bizCount = data.filter((u) => u.occupation_type === "business").length;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Occupation Details</h2>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <SummaryCard label="Total with Occupation" value={data.length} bg="bg-blue-50" border="border-blue-200" />
        <SummaryCard label="Jobs" value={jobCount} bg="bg-indigo-50" border="border-indigo-200" textColor="text-indigo-700" />
        <SummaryCard label="Businesses" value={bizCount} bg="bg-green-50" border="border-green-200" textColor="text-green-700" />
      </div>

      <input
        type="text"
        placeholder="Search by name, sevakcode, role, field..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full md:w-80 px-3 py-2 border rounded-md text-sm"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((u) => {
          const children = parseJson(u.children_details);
          const hasChildren = u.has_children === "yes" && children && children.length > 0;
          const isExpanded = expandedId === u.id;

          return (
            <div key={u.id} className="bg-white rounded-lg shadow border hover:shadow-md transition-shadow">
              <div className="p-4 border-b">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-bold text-gray-900">{u.FIRST_NAME} {u.LAST_NAME}</p>
                    <p className="text-xs font-mono text-purple-600">{u.Sevakcode}</p>
                    <p className="text-xs text-gray-400">{u.DISTRICT || ""}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded font-semibold ${
                    u.occupation_type === "job" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"
                  }`}>
                    {u.occupation_type === "job" ? "Job" : "Business"}
                  </span>
                </div>
              </div>

              <div className="p-4 space-y-2">
                {u.occupation_type === "job" ? (
                  <>
                    <DetailRow label="Company" value={u.job_company_name} />
                    <DetailRow label="Role" value={u.job_role} />
                    {u.job_field && <DetailRow label="Field" value={u.job_field} />}
                    {u.job_company_about && <DetailRow label="About" value={u.job_company_about} />}
                  </>
                ) : (
                  <>
                    <DetailRow label="Business" value={u.business_name} />
                    <DetailRow label="Type" value={u.business_type} />
                    <DetailRow label="Field" value={u.business_field} />
                    {u.business_about && <DetailRow label="About" value={u.business_about} />}
                  </>
                )}
              </div>

              {hasChildren && (
                <div className="border-t">
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : u.id)}
                    className="w-full px-4 py-2 text-sm text-purple-600 hover:bg-purple-50 flex justify-between items-center"
                  >
                    <span>👨‍👩‍👧‍👦 Children ({children.length})</span>
                    <span>{isExpanded ? "▲" : "▼"}</span>
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-3 space-y-2">
                      {children.map((child, i) => (
                        <div key={i} className="p-2 bg-gray-50 rounded border text-sm">
                          <p className="font-semibold text-gray-700">{child.name || `Child ${i + 1}`}</p>
                          {child.occupation_type === "job" ? (
                            <p className="text-gray-500">{child.job_role} at {child.job_company_name}</p>
                          ) : child.occupation_type === "business" ? (
                            <p className="text-gray-500">{child.business_field} — {child.business_name}</p>
                          ) : (
                            <p className="text-gray-400">No occupation details</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-800 text-right">{value || "—"}</span>
    </div>
  );
}